import { useEffect, useState } from 'react'
import LacunaWebPKI from 'web-pki'

import type { Certificado } from '@/models'

type WebPkiExceptionModel = {
  message?: string
  userMessage?: string
  origin?: string
}

interface BuscarCertificadoComando {
  subjectName: string
}

interface LerCertificadoComando {
  thumbprint: string
}

interface AssinarComRestPkiComando {
  thumbprint: string
  token: string
}

export interface LacunaHelperInterface {
  todosCertificados(): Certificado[]
  buscarCertificado(comando: BuscarCertificadoComando): Certificado | undefined
  lerCertificados(comando: LerCertificadoComando): Promise<string>
  assinarComRestPki(comando: AssinarComRestPkiComando): Promise<string>
}

export function useLacuna(): LacunaHelperInterface & {
  inicializado: boolean
  certificados: Certificado[]
} {
  const [inicializado, setInicializado] = useState(false)
  const [certificados, setCertificados] = useState<Certificado[]>([])
  const [lacunaWebPKI] = useState<LacunaWebPKI | null>(() => {
    if (typeof window === 'undefined') return null

    const license = process.env.REACT_APP_LACUNA_WEBPKI_CERTIFICATE
    return license ? new LacunaWebPKI(license) : new LacunaWebPKI()
  })

  useEffect(() => {
    if (!lacunaWebPKI) {
      console.error(
        'Lacuna Web PKI não foi inicializado. Verifique a configuração/licença e se o pacote "web-pki" está instalado.'
      )
      return
    }

    let montado = true

    const onWebPkiReady = () => {
      if (!montado) return

      lacunaWebPKI
        .listCertificates()
        .success((certs: unknown[]) => {
          if (!montado) return

          setCertificados(
            (certs as Certificado[]).map((certificado: Certificado) => ({
              ...certificado,
              titulo: certificado.subjectName,
            }))
          )
          setInicializado(true)
        })
        .fail((ex: WebPkiExceptionModel) => {
          console.error('Falha ao listar certificados', ex)
          setInicializado(false)
        })
    }

    const onWebPkiNaoInstalado = () => {
      if (!montado) return

      lacunaWebPKI.redirectToInstallPage()
    }

    lacunaWebPKI.init({
      ready: onWebPkiReady,
      notInstalled: onWebPkiNaoInstalado,
    })

    return () => {
      montado = false
    }
  }, [lacunaWebPKI])

  const buscarCertificado = ({
    subjectName,
  }: BuscarCertificadoComando): Certificado | undefined => {
    return certificados.find(
      certificado => certificado.subjectName === subjectName
    )
  }

  const lerCertificados = ({
    thumbprint,
  }: LerCertificadoComando): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!lacunaWebPKI) {
        return reject(new Error('Lacuna Web PKI não carregado'))
      }

      lacunaWebPKI
        .readCertificate({ thumbprint })
        .success(resolve)
        .fail((ex: WebPkiExceptionModel) => {
          reject(
            new Error(
              `Lacuna Web PKI: ${ex.userMessage ?? ex.message ?? 'Erro desconhecido'} (origin: ${ex.origin ?? 'unknown'})`
            )
          )
        })
    })
  }

  const assinarComRestPki = ({
    thumbprint,
    token,
  }: AssinarComRestPkiComando): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!lacunaWebPKI) {
        return reject(new Error('Lacuna Web PKI não carregado'))
      }

      lacunaWebPKI
        .signWithRestPki({ thumbprint, token })
        .success(resolve)
        .fail((ex: WebPkiExceptionModel) => {
          reject(
            new Error(
              `Lacuna Web PKI: ${ex.userMessage ?? ex.message ?? 'Erro desconhecido'} (origin: ${ex.origin ?? 'unknown'})`
            )
          )
        })
    })
  }

  const todosCertificados = (): Certificado[] => certificados

  return {
    inicializado,
    certificados,
    todosCertificados,
    buscarCertificado,
    lerCertificados,
    assinarComRestPki,
  }
}
